// filepath: frontend/src/components/layout/AppShell.tsx
import { ReactNode, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Toaster } from 'sonner';
import { Sidebar } from './Sidebar';
import { Header } from './Header';
import { CommandPalette } from '@/components/CommandPalette';
import { useWebSocket } from '@/hooks/useWebSocket';
import { useRealtimeSync } from '@/hooks/useRealtimeSync';
import { useCommandPalette } from '@/hooks/useCommandPalette';
import { useUIStore } from '@/store/uiStore';

interface Props {
  children: ReactNode;
}

export function AppShell({ children }: Props) {
  const navigate = useNavigate();
  const { setIsOnline } = useUIStore();
  useWebSocket();
  useRealtimeSync();
  useCommandPalette();

  useEffect(() => {
    const goOnline = () => setIsOnline(true);
    const goOffline = () => setIsOnline(false);
    window.addEventListener('online', goOnline);
    window.addEventListener('offline', goOffline);
    return () => {
      window.removeEventListener('online', goOnline);
      window.removeEventListener('offline', goOffline);
    };
  }, [setIsOnline]);

  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      // ⌘N / Ctrl+N — dispatch new-entry event (pages handle their own modal)
      if ((e.metaKey || e.ctrlKey) && e.key === 'n') {
        e.preventDefault();
        window.dispatchEvent(new CustomEvent('potopod:new-entry'));
      }
      // ⌘D — download excel
      if ((e.metaKey || e.ctrlKey) && e.key === 'd') {
        e.preventDefault();
        window.dispatchEvent(new CustomEvent('potopod:download-excel'));
      }
      // ⌘H — go to Hub
      if ((e.metaKey || e.ctrlKey) && e.key === 'h') {
        e.preventDefault();
        navigate('/hub');
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [navigate]);

  return (
    <div className="flex h-screen overflow-hidden apple-shell">
      {/* Sidebar */}
      <Sidebar />

      {/* Main area */}
      <div className="flex-1 flex flex-col overflow-hidden px-3 pb-3 pt-2">
        <Header />

        <main
          className="flex-1 overflow-y-auto apple-surface staff-main-content mt-2"
          id="main-content"
          tabIndex={-1}
          aria-label="Main content"
        >
          <motion.div
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.2 }}
            className="h-full app-page-frame"
          >
            {children}
          </motion.div>
        </main>
      </div>

      {/* Global overlays */}
      <CommandPalette />
      <Toaster
        position="bottom-right"
        toastOptions={{
          style: {
            background: 'var(--navy-surface)',
            border: '1px solid rgba(255,255,255,0.08)',
            color: 'white',
          },
        }}
      />
    </div>
  );
}
